import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Languages, AlertTriangle, ImageIcon, Eye, EyeOff, CornerDownRight } from "lucide-react";
import DishDialog from "@/pages/admin/DishDialog";
import CategoryDialog from "@/pages/admin/CategoryDialog";
import { SortableList, SortableRow } from "@/components/SortableList";
import { toast } from "sonner";

const STATUS = {
  active: { label: "Attivo", cls: "bg-emerald-100 text-emerald-700" },
  sold_out: { label: "Esaurito", cls: "bg-amber-100 text-amber-700" },
  hidden: { label: "Nascosto", cls: "bg-gray-200 text-gray-600" },
};

const MenuEditor = () => {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dishesByCat, setDishesByCat] = useState({});
  const [allergens, setAllergens] = useState([]);
  const [dishDialog, setDishDialog] = useState(null);
  const [catDialog, setCatDialog] = useState(null);
  const [menuMeta, setMenuMeta] = useState({ cover_charge: "", cover_text: "", notes: "" });

  const loadDishes = useCallback(async (cats) => {
    const map = {};
    for (const c of cats) {
      const r = await api.get(`/categories/${c.id}/dishes`);
      map[c.id] = r.data;
    }
    setDishesByCat(map);
  }, []);

  const load = useCallback(async () => {
    const [menusRes, catRes, algRes] = await Promise.all([
      api.get("/menus"), api.get(`/menus/${menuId}/categories`), api.get("/allergens"),
    ]);
    setMenus(menusRes.data);
    const cur = menusRes.data.find((m) => m.id === menuId);
    setMenu(cur);
    setMenuMeta({ cover_charge: cur?.cover_charge ?? "", cover_text: cur?.cover_text || "", notes: cur?.notes || "" });
    setCategories(catRes.data);
    setAllergens(algRes.data);
    await loadDishes(catRes.data);
  }, [menuId, loadDishes]);

  useEffect(() => { load(); }, [load]);

  const topCats = categories.filter((c) => !c.parent_id);
  const childrenOf = (id) => categories.filter((c) => c.parent_id === id);

  const deleteCategory = async (c) => {
    const kids = childrenOf(c.id).length;
    const msg = kids ? `Eliminare "${c.name}", le sue ${kids} sottocategorie e tutti i piatti?` : `Eliminare "${c.name}" e i suoi piatti?`;
    if (!window.confirm(msg)) return;
    await api.delete(`/categories/${c.id}`); load();
  };

  const reorderCats = async (subset, ids, next) => {
    const rest = categories.filter((c) => !subset.some((s) => s.id === c.id));
    setCategories([...rest, ...next]);
    await api.put("/categories/reorder", { ids });
  };

  const toggleCatVisible = async (c) => {
    const next = c.visible === false;
    setCategories(categories.map((x) => (x.id === c.id ? { ...x, visible: next } : x)));
    try {
      await api.put(`/categories/${c.id}/visibility`, { visible: next });
      toast.success(next ? "Categoria visibile" : "Categoria nascosta dal pubblico");
    } catch (e) { setCategories(categories); toast.error("Errore"); }
  };
  const reorderDishes = async (catId, ids, next) => { setDishesByCat({ ...dishesByCat, [catId]: next }); await api.put("/dishes/reorder", { ids }); };

  const deleteDish = async (d) => {
    if (!window.confirm(`Eliminare "${d.name}"?`)) return;
    await api.delete(`/dishes/${d.id}`); load();
  };

  const translateDish = async (d) => {
    toast.loading("Traduzione in corso…", { id: "tr" });
    try {
      const res = await api.post(`/dishes/${d.id}/translate`);
      const n = res.data.updated?.length || 0;
      toast.success(n ? `Tradotto (${n} lingue)` : "Già aggiornato", { id: "tr" });
      load();
    } catch (e) { toast.error("Errore traduzione", { id: "tr" }); }
  };

  const saveMenuMeta = async () => {
    const payload = {
      name: menu.name, type: menu.type, is_daily: menu.is_daily,
      cover_image: menu.cover_image, home_visible: menu.home_visible, visible: menu.visible,
      cover_charge: menuMeta.cover_charge === "" ? null : Number(menuMeta.cover_charge),
      cover_text: menuMeta.cover_text.trim() || null,
      notes: menuMeta.notes.trim() || null,
    };
    try { const r = await api.put(`/menus/${menuId}`, payload); setMenu(r.data); toast.success("Coperto e note salvati"); }
    catch (e) { toast.error("Errore"); }
  };

  if (!menu) return <p className="text-muted-foreground">Caricamento…</p>;
  const isDrink = menu.type === "bevande" || menu.type === "misto";
  const staleCount = (d) => Object.values(d.translations || {}).filter((t) => t.stale).length;

  const dishesBlock = (c) => (
    <>
      <SortableList
        items={dishesByCat[c.id] || []}
        onReorder={(ids, next) => reorderDishes(c.id, ids, next)}
        className="divide-y divide-border"
        renderItem={(d) => (
          <SortableRow key={d.id} id={d.id}>
            {({ listeners }) => (
              <div className="flex items-center gap-2 px-3 py-3 bg-white" data-testid={`dish-row-${d.id}`}>
                <button {...listeners} className="drag-handle p-1 text-muted-foreground touch-none" data-testid={`drag-dish-${d.id}`}><GripVertical className="w-4 h-4" /></button>
                {d.image_path ? <img src={fileUrl(d.image_path)} alt={d.name} className="w-11 h-11 rounded-lg object-cover" /> : <div className="w-11 h-11 rounded-lg bg-muted" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{d.name}</p>
                    {d.keep_name && <span title="Nome mantenuto in italiano (non tradotto)" data-testid={`keep-name-badge-${d.id}`} className="text-[10px] font-bold leading-none px-1.5 py-1 rounded bg-[#002FA7]/10 text-[#002FA7] flex-shrink-0">IT</span>}
                    {staleCount(d) > 0 && <span title="Traduzioni da aggiornare"><AlertTriangle className="w-3.5 h-3.5 text-amber-500" /></span>}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS[d.status]?.cls}`}>{STATUS[d.status]?.label}</span>
                    {d.price != null && <span className="text-xs text-muted-foreground">€ {d.price.toFixed(2)}</span>}
                    {d.allergens?.length > 0 && <span className="text-xs text-muted-foreground">· {d.allergens.length} all.</span>}
                  </div>
                </div>
                <button onClick={() => translateDish(d)} className="p-2 rounded-lg hover:bg-muted text-[#002FA7]" data-testid={`translate-dish-${d.id}`} title="Traduci con AI"><Languages className="w-4 h-4" /></button>
                <button onClick={() => setDishDialog({ catId: c.id, dish: d })} className="p-2 rounded-lg hover:bg-muted text-muted-foreground" data-testid={`edit-dish-${d.id}`}><Pencil className="w-4 h-4" /></button>
                <button onClick={() => deleteDish(d)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`delete-dish-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            )}
          </SortableRow>
        )}
      />
      <button onClick={() => setDishDialog({ catId: c.id, dish: null })} data-testid={`add-dish-${c.id}`} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#002FA7] hover:bg-muted/40 transition-colors border-t border-border">
        <Plus className="w-4 h-4" /> Aggiungi {isDrink ? "drink" : "piatto"}
      </button>
    </>
  );

  const catHeader = (c, listeners, isChild) => (
    <div className={`flex items-center gap-2 px-3 py-3 border-b border-border bg-muted/30 ${c.visible === false ? "opacity-60" : ""}`}>
      {listeners ? <button {...listeners} className="drag-handle p-1 text-muted-foreground touch-none" data-testid={`drag-cat-${c.id}`}><GripVertical className="w-5 h-5" /></button> : <span className="p-1 text-muted-foreground"><CornerDownRight className="w-4 h-4" /></span>}
      {c.cover_image ? <img src={fileUrl(c.cover_image)} alt={c.name} className="w-9 h-9 rounded-lg object-cover" /> : <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center text-muted-foreground"><ImageIcon className="w-4 h-4" /></div>}
      <h3 className={`font-semibold flex-1 truncate ${c.visible === false ? "line-through" : ""}`}>{c.name}{c.visible === false && <span className="ml-2 align-middle text-xs font-normal no-underline text-muted-foreground">nascosta</span>}</h3>
      <button onClick={() => toggleCatVisible(c)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid={`toggle-cat-visible-${c.id}`} title={c.visible === false ? "Rendi visibile" : "Nascondi"}>{c.visible === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
      <button onClick={() => setCatDialog({ category: c, parentId: null })} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground" data-testid={`edit-cat-${c.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => deleteCategory(c)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive" data-testid={`delete-cat-${c.id}`}><Trash2 className="w-4 h-4" /></button>
    </div>
  );

  return (
    <div>
      <button onClick={() => navigate("/admin")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-menus"><ArrowLeft className="w-4 h-4" /> Menù</button>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-cabinet font-bold text-3xl tracking-tight">{menu.name}</h1>
        <button onClick={() => setCatDialog({ category: null, parentId: null })} data-testid="add-category-btn" className="rounded-full bg-[#002FA7] text-white font-semibold px-5 py-2.5 flex items-center gap-2 hover:bg-[#00248a]"><Plus className="w-4 h-4" /> <span className="hidden sm:inline">Categoria</span></button>
      </div>
      <p className="text-muted-foreground text-sm mb-4">Trascina <GripVertical className="inline w-3.5 h-3.5" /> per riordinare. Usa “Sottocategoria” per annidare (es. Aggiunte dentro Pizze).</p>

      <SortableList
        items={topCats}
        onReorder={(ids, next) => reorderCats(topCats, ids, next)}
        className="space-y-5"
        renderItem={(c) => (
          <SortableRow key={c.id} id={c.id}>
            {({ listeners }) => (
              <div className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`category-${c.id}`}>
                {catHeader(c, listeners, false)}
                {dishesBlock(c)}

                {childrenOf(c.id).length > 0 && (
                  <div className="pl-4 sm:pl-6 pr-2 py-3 bg-muted/10 border-t border-border space-y-3">
                    <SortableList
                      items={childrenOf(c.id)}
                      onReorder={(ids, next) => reorderCats(childrenOf(c.id), ids, next)}
                      className="space-y-3"
                      renderItem={(sc) => (
                        <SortableRow key={sc.id} id={sc.id}>
                          {({ listeners: subListeners }) => (
                            <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid={`subcategory-${sc.id}`}>
                              {catHeader(sc, subListeners, true)}
                              {dishesBlock(sc)}
                            </div>
                          )}
                        </SortableRow>
                      )}
                    />
                  </div>
                )}

                <button onClick={() => setCatDialog({ category: null, parentId: c.id })} data-testid={`add-subcat-${c.id}`} className="w-full flex items-center gap-2 px-4 py-2.5 text-xs font-semibold text-muted-foreground hover:bg-muted/40 transition-colors border-t border-border">
                  <CornerDownRight className="w-3.5 h-3.5" /> Aggiungi sottocategoria
                </button>
              </div>
            )}
          </SortableRow>
        )}
      />
      {topCats.length === 0 && <p className="text-muted-foreground text-center py-10">Aggiungi una categoria per iniziare.</p>}

      <section className="mt-8 bg-white rounded-2xl border border-border p-5" data-testid="menu-extra-section">
        <h3 className="font-cabinet font-semibold text-lg mb-1">Coperto e note</h3>
        <p className="text-sm text-muted-foreground mb-4">Mostrati in fondo a questo menù nel menu pubblico. Il testo viene tradotto automaticamente con le altre voci (dal report traduzioni in Impostazioni).</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Prezzo coperto (€)</label>
            <input type="number" step="0.01" min="0" value={menuMeta.cover_charge} onChange={(e) => setMenuMeta({ ...menuMeta, cover_charge: e.target.value })} data-testid="menu-cover-charge" placeholder="Es. 3.00" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <label className="text-sm font-medium">Etichetta coperto <span className="text-xs text-muted-foreground font-normal">(opzionale)</span></label>
            <input value={menuMeta.cover_text} onChange={(e) => setMenuMeta({ ...menuMeta, cover_text: e.target.value })} data-testid="menu-cover-text" placeholder="Es. Coperto / Servizio incluso" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-sm font-medium">Note per i clienti <span className="text-xs text-muted-foreground font-normal">(opzionale)</span></label>
          <textarea rows={3} value={menuMeta.notes} onChange={(e) => setMenuMeta({ ...menuMeta, notes: e.target.value })} data-testid="menu-notes" placeholder="Es. Avvisi su allergeni, orari, informazioni sul locale…" className="mt-1 w-full rounded-xl border border-input px-4 py-2.5 focus:outline-none focus:border-[#002FA7]" />
        </div>
        <div className="flex justify-end mt-4">
          <button onClick={saveMenuMeta} data-testid="save-menu-extra" className="rounded-full bg-[#002FA7] text-white px-5 py-2 text-sm font-semibold hover:bg-[#00248a]">Salva coperto e note</button>
        </div>
      </section>

      {dishDialog && (
        <DishDialog open={!!dishDialog} onClose={() => setDishDialog(null)} onSaved={() => { setDishDialog(null); load(); }} catId={dishDialog.catId} menuId={menuId} menus={menus} dish={dishDialog.dish} allergens={allergens} isDrink={isDrink} />
      )}
      {catDialog && (
        <CategoryDialog
          open={!!catDialog}
          onClose={() => setCatDialog(null)}
          onSaved={() => { setCatDialog(null); load(); }}
          menuId={menuId}
          menus={menus}
          category={catDialog.category}
          parentId={catDialog.parentId}
          hasChildren={!!(catDialog.category && childrenOf(catDialog.category.id).length > 0)}
        />
      )}
    </div>
  );
};

export default MenuEditor;
